<template>
  <div>
    <!-- Step progress bar -->
    <step-progress-bar
      :steps="stepBarItems"
      :current-step="currentStep"
      class="mb-2"
    />
    <div
      data-testid="section-verify-contract"
      class="verify-contract"
    >

      <!-- Step 1 -->
      <contract-info-verification-step1
        v-if="currentStep === 1"
        @next="handleStep1Complete"
      />

      <!-- Step 2: Customer Information -->
      <contract-info-verification-step2
        v-if="currentStep === 2"
        ref="step2"
        :form="step2Form"
        :options="step2Options"
        :has-disabled="hasDisabled"
        @update="applyUpdate"
        @back="goPrevStep"
        @next="onStep2Next"
      />

      <!-- Step 3: Contact Information -->
      <contract-info-verification-step3
        v-if="currentStep === 3"
        ref="step3"
        :form="step3Form"
        :options="step3Options"
        :has-disabled="hasDisabled"
        :loading="submitLoading"
        @update="applyUpdate"
        @back="goPrevStep"
        @submit="validationForm"
      />
    </div>
  </div>
</template>

<script>
import { createNamespacedHelpers, mapGetters } from 'vuex'
import { toastification } from '@core/mixins/toast'
import stepGuideMixin from '@/mixins/stepGuideMixin'
import StepProgressBar from '@/components/StepProgressBar.vue'
import { CUSTOMER_TYPE, CUSTOMER_TYPE_OPTIONS } from '@/constants/customerType'
import {
  ID_TYPE_INDIVIDUAL, ID_TYPE_BUSINESS, ID_TYPE_OPTIONS_INDIVIDUAL, ID_TYPE_OPTIONS_BUSINESS, GENDER_OPTIONS, V_CONTRACT_STATUS,
} from '@/constants/contractVerification'
import ContractInfoVerificationStep1 from './ContractInfoVerificationStep1.vue'
import ContractInfoVerificationStep2 from './ContractInfoVerificationStep2.vue'
import ContractInfoVerificationStep3 from './ContractInfoVerificationStep3.vue'

const { mapActions } = createNamespacedHelpers('auth')
const { mapGetters: mapGettersContract, mapActions: mapActionsContract } = createNamespacedHelpers('contract')

export default {
  name: 'VerifyContract',

  components: {
    StepProgressBar,
    ContractInfoVerificationStep1,
    ContractInfoVerificationStep2,
    ContractInfoVerificationStep3,
  },

  mixins: [toastification, stepGuideMixin],

  data() {
    return {
      currentStep: 1,
      customerType: null,
      customer: '',
      dateOfBirth: null,
      idType: null,
      idNumber: '',
      gender: null,
      taxCode: '',
      representative: '',
      position: '',
      issueDate: null,
      issuePlace: '',

      // Contact information
      useNewAdministrativeArea: true,
      province: null,
      ward: null,
      district: null,
      address: '',
      contactEmail: '',
      contactPhone: '',
      zaloNumber: '',
      checked: false,
      phoneData: {
        countryAbbr: '',
        countryCode: '',
        phone: '',
        isValid: true,
        isRequired: false,
      },
      isErrorPhoneNumber: false,
      // District list (can be fetched from API)
      optionsDistrict: [],

      // Business customer profile
      businessLicensePage1Url: '',
      businessLicensePage2Url: '',
      businessLicensePage1Name: '',
      businessLicensePage2Name: '',
      uploadingField: null,
      hasFilledContract: false,
      submitLoading: false,
    }
  },

  computed: {
    ...mapGetters({
      status: 'auth/status',
      loading: 'auth/loading',
      message: 'auth/message',
      user: 'auth/user',
    }),
    ...mapGettersContract({
      provincesOptions: 'provincesOptions',
      wardsOptions: 'wardsOptions',
      contractLoading: 'loading',
      contractStatus: 'status',
      contractMessage: 'message',
      contract: 'contract',
    }),

    step2Form() {
      return {
        customerType: this.customerType,
        customer: this.customer,
        dateOfBirth: this.dateOfBirth,
        idType: this.idType,
        idNumber: this.idNumber,
        gender: this.gender,
        taxCode: this.taxCode,
        representative: this.representative,
        position: this.position,
        issueDate: this.issueDate,
        issuePlace: this.issuePlace,
        businessLicensePage1Url: this.businessLicensePage1Url,
        businessLicensePage2Url: this.businessLicensePage2Url,
        businessLicensePage1Name: this.businessLicensePage1Name,
        businessLicensePage2Name: this.businessLicensePage2Name,
        uploadingField: this.uploadingField,
      }
    },

    step3Form() {
      return {
        useNewAdministrativeArea: this.useNewAdministrativeArea,
        province: this.province,
        district: this.district,
        ward: this.ward,
        address: this.address,
        contactEmail: this.contactEmail,
        contactPhone: this.contactPhone,
        zaloNumber: this.zaloNumber,
        checked: this.checked,
        phoneData: this.phoneData,
      }
    },

    step2Options() {
      return {
        customerType: this.optionsCustomerType,
        idType: this.optionsIdType,
        gender: this.optionsGender,
      }
    },

    step3Options() {
      return {
        province: this.provincesOptions,
        district: this.optionsDistrict,
        ward: this.wardsOptions,
      }
    },

    stepBarItems() {
      const currentStepNum = this.currentStep

      const getStep1Status = () => {
        if (this.hasContract) {
          return currentStepNum === 1 ? 'current' : 'completed'
        }
        if (currentStepNum === 1) return 'current'
        if (currentStepNum > 1) return 'completed'
        return 'pending'
      }

      const getStep2Status = () => {
        if (this.hasContract) {
          return currentStepNum === 2 ? 'current' : 'completed'
        }
        if (currentStepNum === 2) return 'current'
        if (currentStepNum > 2) return 'completed'
        if (currentStepNum > 1) return 'active'
        return 'pending'
      }

      const getStep3Status = () => {
        if (this.hasContract) {
          return currentStepNum === 3 ? 'current' : 'completed'
        }
        if (currentStepNum === 3) return 'current'
        if (currentStepNum > 2) return 'active'
        return 'pending'
      }

      return [
        {
          icon: 'PackageIcon',
          label: this.$t(
            'stepGuideControl.stepVerifyInfo.labelAccountInfo',
          ),
          status: getStep1Status(),
        },
        {
          icon: 'ShieldIcon',
          label: this.$t(
            'stepGuideControl.stepVerifyInfo.formVerifyContract.stepBarCustomerInfo',
          ),
          status: getStep2Status(),
        },
        {
          icon: 'PhoneIcon',
          label: this.$t(
            'stepGuideControl.stepVerifyInfo.formVerifyContract.stepBarContactInfo',
          ),
          status: getStep3Status(),
        },
      ]
    },

    hasDisabled() {
      const contractStatus = this.contract?.contract?.status
      return contractStatus === V_CONTRACT_STATUS.PROCESSING || contractStatus === V_CONTRACT_STATUS.FINISHED
    },

    isIndividual() {
      return this.customerType?.value === CUSTOMER_TYPE.INDIVIDUAL
    },

    isBusiness() {
      return this.customerType?.value === CUSTOMER_TYPE.BUSINESS
    },

    isHousehold() {
      return this.customerType?.value === CUSTOMER_TYPE.HOUSEHOLD
    },

    isBusinessOrHousehold() {
      return this.isBusiness || this.isHousehold
    },

    optionsCustomerType() {
      return CUSTOMER_TYPE_OPTIONS.map(option => ({
        label: this.$t(option.labelKey),
        value: option.value,
      }))
    },

    optionsIdType() {
      // If Business or Household, use different options
      if (this.isBusiness || this.isHousehold) {
        return ID_TYPE_OPTIONS_BUSINESS.map(option => ({
          label: this.$t(option.labelKey),
          value: option.value,
        }))
      }
      // Default to Individual options
      return ID_TYPE_OPTIONS_INDIVIDUAL.map(option => ({
        label: this.$t(option.labelKey),
        value: option.value,
      }))
    },

    optionsGender() {
      return GENDER_OPTIONS.map(option => ({
        text: this.$t(option.textKey),
        value: option.value,
      }))
    },

    hasContract() {
      return !!(this.contract?.contract?.contractId || this.contract?.customer)
    },
  },

  watch: {
    customerType(newVal, oldVal) {
      // Reset values when switching customer type
      if (newVal?.value) {
        if (newVal?.value === CUSTOMER_TYPE.INDIVIDUAL) {
          if (oldVal && oldVal?.value !== CUSTOMER_TYPE.INDIVIDUAL) {
            this.taxCode = ''
            this.representative = ''
            this.position = ''
            this.businessLicensePage1Url = ''
            this.businessLicensePage1Name = ''
            this.businessLicensePage2Url = ''
            this.businessLicensePage2Name = ''
          }
          // Set idType to CCCD for individual
          this.idType = {
            label: this.$t('stepGuideControl.stepVerifyInfo.formVerifyContract.optionIdTypeCCCD'),
            value: ID_TYPE_INDIVIDUAL.CCCD,
          }
        } else if (newVal?.value === CUSTOMER_TYPE.BUSINESS || newVal?.value === CUSTOMER_TYPE.HOUSEHOLD) {
          if (oldVal && oldVal?.value !== CUSTOMER_TYPE.BUSINESS && oldVal?.value !== CUSTOMER_TYPE.HOUSEHOLD) {
            this.gender = null
            this.dateOfBirth = null
          }
          // Set default idType to Business License for business
          this.idType = {
            label: this.$t('stepGuideControl.stepVerifyInfo.formVerifyContract.optionIdTypeBusinessLicense'),
            value: ID_TYPE_BUSINESS.BUSINESS_LICENSE,
          }
        }
      }
    },

    province(newVal) {
      // Reset ward and district when province changes
      this.ward = null
      this.district = null
      // Call API to get wards list based on province
      if (newVal?.code) {
        this.getWards(newVal.code)
      }
    },

    district() {
      // Reset ward when district changes
      this.ward = null
    },

    useNewAdministrativeArea(newVal) {
      // Reset values when toggling
      if (newVal) {
        // When enabled: reset district
        this.district = null
        // Reload wards based on province
        if (this.province?.code) {
          this.getWards(this.province.code)
        }
      } else {
        // When disabled: reset ward
        this.ward = null
      }
    },

    user: {
      handler(userInfo) {
        if (userInfo?.data) {
          // Fill email from user data or contactInfo
          if (userInfo?.data?.contactInfo?.contactEmail) {
            this.contactEmail = userInfo?.data?.contactInfo?.contactEmail
          } else if (userInfo?.data?.email && !this.contactEmail) {
            this.contactEmail = userInfo?.data?.email
          }

          if (userInfo?.data?.contactInfo) {
            this.zaloNumber = userInfo?.data?.contactInfo?.zalo || ''

            if (userInfo?.data?.contactInfo?.phone) {
              const [
                countryAbbr,
                countryCode,
                phoneNum,
              ] = userInfo?.data?.contactInfo?.phone?.split('|')

              this.phoneData.phone = phoneNum || ''
              this.phoneData.countryAbbr = countryAbbr || 'vn'
              this.phoneData.countryCode = countryCode || ''

              if (this.zaloNumber === `+${this.phoneData.countryCode} ${this.phoneData.phone}`) {
                this.checked = true
              }
            }
          }
        }
      },
      deep: true,
      immediate: true,
    },

    phoneData: {
      handler(val) {
        if (this.checked) {
          this.zaloNumber = `+${val.countryCode} ${val.phone}`
        }
        this.isErrorPhoneNumber = false
      },
      deep: true,
    },

    checked(val) {
      if (val) {
        this.zaloNumber = `+${this.phoneData.countryCode} ${this.phoneData.phone}`
      } else {
        this.zaloNumber = ''
      }
    },

    contract: {
      handler(contractData) {
        if (contractData && !this.hasFilledContract) {
          this.fillContractData(contractData)
          this.hasFilledContract = true
        }
      },
      immediate: true,
    },
  },

  created() {
    // Set default Document Type to CCCD (for individual)
    // Will be updated when customerType changes
    this.idType = {
      label: this.$t('stepGuideControl.stepVerifyInfo.formVerifyContract.optionIdTypeCCCD'),
      value: ID_TYPE_INDIVIDUAL.CCCD,
    }
  },

  mounted() {
    const step = Number(this.$route.query.step)
    if (step >= 1 && step <= 3) {
      this.currentStep = step
    }
  },

  methods: {
    ...mapActions(['updateAccount', 'getAccountInfo']),
    ...mapActionsContract(['createContract', 'updateContract', 'getWards', 'getContract']),

    validPhoneInput() {
      return !this.phoneData.phone || !this.phoneData.isValid
    },

    applyUpdate(patch) {
      if (!patch || typeof patch !== 'object') return
      Object.keys(patch).forEach(k => {
        if (Object.prototype.hasOwnProperty.call(patch, k)) {
          this[k] = patch[k]
        }
      })
    },

    handleStep1Complete() {
      this.currentStep = 2
    },

    async onStep2Next() {
      const ok = await this.$refs.step2.validate()
      if (ok) this.currentStep = 3
    },

    goPrevStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1
      }
    },

    formatDateToYYYYMMDD(dateString) {
      if (!dateString) return null
      // Convert from d/m/Y to YYYY-MM-DD
      const parts = dateString.split('/')
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0')
        const month = parts[1].padStart(2, '0')
        const year = parts[2]
        return `${year}-${month}-${day}`
      }
      return dateString
    },

    formatDateToDDMMYYYY(dateString) {
      if (!dateString) return null
      // Convert from YYYY-MM-DD to d/m/Y
      const parts = dateString.split('-')
      if (parts.length === 3) {
        const year = parts[0]
        const month = parts[1]
        const day = parts[2]
        return `${day}/${month}/${year}`
      }
      return dateString
    },

    fillContractData(contract) {
      // Support both old format (customerInfo) and new format (customer)
      const customerData = contract?.customer || contract?.customerInfo
      if (!contract || !customerData || this.hasFilledContract) return

      this.hasFilledContract = true

      // Step 2: Customer Information
      if (customerData.cusName) {
        this.customer = customerData.cusName
      }

      // Customer Type - set first as ID Type depends on it
      if (customerData.identityInfo?.customerType) {
        const customerTypeValue = String(customerData.identityInfo.customerType)
        const customerTypeOption = this.optionsCustomerType.find(
          opt => opt.value === customerTypeValue,
        )
        if (customerTypeOption) {
          this.customerType = customerTypeOption
        }
      }

      // ID Type - need to set after customerType is set and computed is updated
      if (customerData.identityInfo?.documentType) {
        this.$nextTick(() => {
          const documentTypeValue = String(customerData.identityInfo.documentType)
          const idTypeOptions = this.optionsIdType
          const idTypeOption = idTypeOptions.find(opt => opt.value === documentTypeValue)
          if (idTypeOption) {
            this.idType = idTypeOption
          }
        })
      }

      // ID Number
      if (customerData.identityInfo?.documentNumber) {
        this.idNumber = customerData.identityInfo.documentNumber
      }

      // Issue Date
      if (customerData.identityInfo?.documentIssuedDate) {
        this.issueDate = this.formatDateToDDMMYYYY(customerData.identityInfo.documentIssuedDate)
      }

      // Issue Place
      if (customerData.identityInfo?.documentIssuedPlace) {
        this.issuePlace = customerData.identityInfo.documentIssuedPlace
      }

      // Tax Code (for business/household)
      if (customerData.identityInfo?.taxCode) {
        this.taxCode = customerData.identityInfo.taxCode
      }

      // Date of Birth (for individual)
      if (customerData.birth) {
        this.dateOfBirth = this.formatDateToDDMMYYYY(customerData.birth)
      }

      // Gender (for individual) - radio button needs string value, not object
      // Set after customerType to ensure it's only set for individual
      if (customerData.sex) {
        this.$nextTick(() => {
          // Set the string value directly for radio button
          this.gender = customerData.sex
        })
      }

      // Representative (for business/household)
      if (customerData.represent?.fullName) {
        this.representative = customerData.represent.fullName
      }

      if (customerData.represent?.position) {
        this.position = customerData.represent.position
      }

      // Step 3: Contact Information
      if (customerData.contactInfo) {
        const { contactInfo } = customerData

        // Email
        if (contactInfo.email) {
          this.contactEmail = contactInfo.email
        }

        // Address
        if (contactInfo.address) {
          this.address = contactInfo.address
        }

        // Province
        if (contactInfo.city?.code) {
          const provinceOption = this.provincesOptions.find(
            opt => opt.value === contactInfo.city.code,
          )
          if (provinceOption) {
            this.province = provinceOption
            // Load wards for the selected province
            if (provinceOption.code) {
              this.getWards(provinceOption.code).then(() => {
                // Set ward after wards are loaded
                if (contactInfo.ward?.code) {
                  this.$nextTick(() => {
                    const wardOption = this.wardsOptions.find(
                      opt => opt.value === contactInfo.ward.code,
                    )
                    if (wardOption) {
                      this.ward = wardOption
                    }
                  })
                }
              })
            }
          }
        }

        // Phone Locale
        if (contactInfo.phoneLocale) {
          const [countryAbbr, countryCode, phone] = contactInfo.phoneLocale.split('|')
          this.phoneData = {
            countryAbbr: (countryAbbr || 'vn').toLowerCase(),
            countryCode: countryCode?.replace(/^\+/, '') || '',
            phone: phone || '',
            isValid: true,
            isRequired: false,
          }
        }
      }
    },

    async validationForm(invalid) {
      const success = await this.$refs.step3.validate()

      const allowedCustomerTypes = [
        CUSTOMER_TYPE.INDIVIDUAL,
        CUSTOMER_TYPE.BUSINESS,
        CUSTOMER_TYPE.HOUSEHOLD,
      ]

      if (
        success
        && !invalid
        && !this.validPhoneInput()
        && allowedCustomerTypes.includes(this.customerType?.value)
      ) {
        this.submitLoading = true

        // Build city object
        const cityObj = this.province?.code ? {
          code: this.province.code,
          ...(this.isBusinessOrHousehold && {
            name: this.province.name || this.province.fullName,
          }),
        } : null

        // Build ward object
        const wardObj = this.ward?.code ? {
          code: this.ward.code,
          ...(this.isBusinessOrHousehold && {
            name: this.ward.name || this.ward.fullName,
          }),
        } : null

        // Build identityInfo
        const identityInfo = {
          customerType: parseInt(this.customerType?.value, 10),
          documentType: parseInt(this.idType?.value, 10),
          documentNumber: this.idNumber,
          ...(this.issueDate && {
            documentIssuedDate: this.formatDateToYYYYMMDD(this.issueDate),
          }),
          ...(this.issuePlace && {
            documentIssuedPlace: this.issuePlace,
          }),
          ...(this.isBusinessOrHousehold && this.taxCode && {
            taxCode: this.taxCode,
          }),
        }

        // Build represent object for business/household
        const representGender = typeof this.gender === 'string' ? this.gender : this.gender?.value
        const representObj = this.isBusinessOrHousehold && this.representative ? {
          fullName: this.representative,
          ...(this.position && { position: this.position }),
          ...(representGender && { sex: representGender }),
          ...(this.dateOfBirth && {
            birth: this.formatDateToYYYYMMDD(this.dateOfBirth),
          }),
        } : null

        let phoneLocale = ''
        if (this.phoneData?.phone) {
          const countryAbbr = (this.phoneData.countryAbbr || 'vn').toLowerCase()
          const countryCode = String(this.phoneData.countryCode || '')
          phoneLocale = `${countryAbbr}|${countryCode}|${this.phoneData.phone}`
        }
        const params = {
          customerInfo: {
            cusName: this.customer,
            contactInfo: {
              fullName: this.customer,
              email: this.contactEmail,
              ...(phoneLocale && { phoneLocale }),
              address: this.address,
              ...(cityObj && { city: cityObj }),
              ...(wardObj && { ward: wardObj }),
            },
            identityInfo,
            ...(this.isIndividual && this.dateOfBirth && {
              birth: this.formatDateToYYYYMMDD(this.dateOfBirth),
            }),
            ...(this.isIndividual && this.gender && {
              sex: this.gender,
            }),
            ...(representObj && { represent: representObj }),
          },
        }

        // Check if contract exists to determine create or update
        const hasContract = this.contract?.contract?.contractId || this.contract?.customer

        if (hasContract) {
          await this.updateContract(params)
        } else {
          await this.createContract(params)
        }

        if (this.contractStatus) {
          this.toastSuccess(
            this.$t(
              'stepGuideControl.stepVerifyInfo.formVerifyContract.textUpdateSuccess',
            ),
          )
          this.$router.push('/waiting-verify-information')
          await Promise.allSettled([
            this.getAccountInfo(),
            this.getContract(),
          ])
          this.$refs.step3.reset()
        } else {
          this.toastFailure(this.contractMessage || this.message || 'An error occurred')
        }

        this.submitLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$font-size-section: 18px;
$font-size-body: 14px;
$font-size-caption: 13px;

.input-height {
  height: 48px;
}

.verify-contract {
  height: 100%;
  background-color: #ffffff;
  border-radius: var(--border-radius-base);
  padding: 24px;

  @media (max-width: 767px) {
    padding: 20px;
  }

  .group-input {
    margin-bottom: 32px;
  }

  .top-content {
    margin-bottom: 32px;
  }

  .input-height {
    height: 48px;
  }

  .btn-control {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    width: 100%;

    &--group {
      justify-content: flex-end;
      gap: 16px;
    }

    .btn-back-inline {
      border: none !important;
      background: transparent !important;
      color: #6e44ff;
      padding: 0;
      text-decoration: none !important;
      font-size: $font-size-body;
      font-weight: 600;

      &:hover:not(:disabled) {
        color: #5a38e0 !important;
        background: transparent !important;
        border: none !important;
        text-decoration: none !important;
      }

      &:disabled {
        color: #c4c4c4;
      }
    }

    .btn-decline,
    .btn-continue {
      width: max-content;
      font-size: $font-size-body;
      margin: 10px 6px;
    }
  }
}
</style>
